import { NestedTreeControl } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  resource,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ClientSearchService } from '@services/client-search.service';
import { ClientService } from '@services/client.service';
import { FormTemplateService } from '@services/forms/form-template.service';
import { SnackbarService } from '@services/snackbar.service';

type MenuData = {
  title: string;
  slug: string;
  path: string;
  hiddenInMenu: boolean;
  children: MenuData[];
};

@Component({
  standalone: true,
  selector: 'app-hd-sanity-diff',
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    MatButtonModule,
    MatIconButton,
    MatSuffix,
    MatFormField,
    MatInput,
    MatLabel,
    MatToolbar,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatTreeModule,
  ],
  templateUrl: './hd-sanity-diff.component.html',
  styleUrl: './hd-sanity-diff.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdSanityDiffComponent {
  private searchService = inject(ClientSearchService);
  private clientService = inject(ClientService);
  private snackbarService = inject(SnackbarService);
  private sanitizer = inject(DomSanitizer);
  private cd = inject(ChangeDetectorRef);
  private formTemplateService = inject(FormTemplateService);

  CDN_AEM_ROOT = 'https://cdn.creekmore.marketing/hd-aem-archive';
  S3_AEM_ROOT = 'https://cmm-hd-aem-archive.s3.us-east-1.amazonaws.com';

  domainHdUrl = signal('');
  loadedDomainHdUrl = signal<SafeResourceUrl>('');
  loadedDomainHdUrlString = signal('');
  sitemapHd = computed(() => {
    const domain = this.domainHdUrl();
    if (!domain) {
      return '';
    }

    return `${domain}/sitemap.xml`;
  });

  domainAemRootPath = signal('');
  domainAemCurrentPath = signal('');
  loadedAemFullPath = signal('');
  loadedAemJson = resource({
    request: () => ({
      path: this.loadedAemFullPath(),
    }),
    loader: (params) => {
      if (!params.request.path) {
        return Promise.resolve('');
      }

      return fetch(
        `${this.CDN_AEM_ROOT}/content/dealer/en/${params.request.path}.json`,
      )
        .then((r) => r.json())
        .catch(() => null);
    },
  });
  sitemapAem = computed(() => {
    const rootPath = this.domainAemRootPath();
    if (!rootPath) {
      return '';
    }

    return `${this.CDN_AEM_ROOT}/content/dealer/en/${rootPath}/sitemap.xml`;
  });
  backupAwsAem = computed(() => {
    const awsUrl =
      'https://us-east-1.console.aws.amazon.com/s3/buckets/cmm-hd-aem-archive';

    const awsParams = new URLSearchParams({
      region: 'us-east-1',
      bucketType: 'general',
      showversions: 'false',
      tab: 'objects',
      prefix: `hd-aem-archive/content/dealer/en/${this.domainAemRootPath().replace('/home', '')}/`,
    });

    return `${awsUrl}?${awsParams.toString()}`;
  });

  @ViewChild('aemMenu') aemMenuTemplate: TemplateRef<any> | undefined;
  menuTreeControl = new NestedTreeControl<MenuData>((page) => page.children);
  menuDataSource = new MatTreeNestedDataSource<MenuData>();

  dynamicFetchedData = signal<Record<string, any>>({});

  getClientAEMPath(pathAndDomain?: boolean): void {
    this.searchService
      .selectFromSearchDialog({
        mode: 'select',
        multiple: false,
        headerText: 'Get HD AEM Path',
      })
      .subscribe((businessList) => {
        if (!businessList) return;

        if (businessList.length === 0) {
          this.domainAemRootPath.set('');
        }

        this.clientService
          .getBusiness(businessList[0])
          .subscribe((business) => {
            if (!business) {
              this.snackbarService.openSnackBar({
                message: 'Error fetching business',
                action: '',
                error: true,
              });

              return;
            }

            let foundAemDomain = business.domains?.find(
              (dom) => dom.cms_platform_id === 1 || dom.cms_platform_id === 30,
            );

            if (
              !foundAemDomain ||
              !foundAemDomain.cms_platform_code ||
              !foundAemDomain.aem_folder_name
            ) {
              this.snackbarService.openSnackBar({
                message: 'An HD AEM path could not be found',
                action: '',
                error: true,
              });

              return;
            }

            this.domainAemRootPath.set(
              `${foundAemDomain.aem_folder_name}-${foundAemDomain.cms_platform_code}/home`,
            );

            if (pathAndDomain || !this.domainHdUrl()) {
              this.domainHdUrl.set(
                `http${foundAemDomain.ssl ? 's' : ''}://${foundAemDomain.use_www ? 'www.' : ''}${foundAemDomain.domain}`,
              );
            }
          });
      });
  }

  navigate() {
    const hdUrl = this.domainHdUrl();
    if (
      hdUrl &&
      (hdUrl.startsWith('http://') || hdUrl.startsWith('https://'))
    ) {
      this.loadedDomainHdUrl.set(
        this.sanitizer.bypassSecurityTrustResourceUrl(hdUrl),
      );
      this.loadedDomainHdUrlString.set(hdUrl);
    }

    this.loadedAemFullPath.set(
      [this.domainAemRootPath(), this.domainAemCurrentPath()]
        .filter((d) => !!d)
        .join('/'),
    );
  }

  getChildComponents(
    component: Record<string, any> | undefined,
    selectedChildren?: string[],
  ): Record<string, any>[] {
    if (!component) {
      return [];
    }

    return Object.entries(component)
      .map(([key, sub]) => {
        if (
          key.startsWith('jcr:') ||
          key.startsWith('cq:') ||
          key.startsWith('sling:') ||
          key === '_cmm_component_type' ||
          key === 'popupModal' ||
          key === 'layout' ||
          key === 'showPopupModalOnExit' ||
          key === 'id'
        ) {
          return null;
        }

        if (typeof sub !== 'object') {
          return null;
        }

        const type = key.split('_')[0];

        if (selectedChildren && !selectedChildren.includes(type)) {
          return null;
        }

        sub._cmm_component_type = type;

        return sub;
      })
      .filter((d) => !!d);
  }

  getFontSize(titleType?: string) {
    const fontTypeNumber = parseInt(titleType?.substring(1, 1) ?? '1') || 3;

    let fontSize = 20;
    switch (fontTypeNumber) {
      case 1:
        fontSize = 28;
        break;
      case 2:
        fontSize = 25;
        break;
      case 3:
        fontSize = 22;
        break;
      case 4:
        fontSize = 18;
        break;
      case 5:
      default:
        fontSize = 15;
        break;
    }

    return `font-size: ${fontSize}px;`;
  }

  updateRelativeSrc(html?: string): string {
    return html?.replaceAll(/src="\//g, `src="${this.CDN_AEM_ROOT}/`) ?? '';
  }

  async fetchAltText(imagePath?: string) {
    if (!imagePath) {
      return;
    }

    const dynamicData = this.dynamicFetchedData();
    if (imagePath in dynamicData) {
      return;
    }
    dynamicData[imagePath] = null;

    console.log(imagePath);

    const response = await fetch(`${this.CDN_AEM_ROOT}${imagePath}.json`);

    if (!response.ok) {
      return;
    }

    const json = await response.json();

    this.dynamicFetchedData.update((responses) => {
      responses[imagePath] =
        json.metadata['dc:title'] ??
        json.metadata['dc:description'] ??
        '(none)';

      return responses;
    });

    this.cd.detectChanges();
  }

  async fetchGalleryImages(rootPath?: string) {
    if (!rootPath) {
      return;
    }

    const dynamicData = this.dynamicFetchedData();
    if (rootPath in dynamicData) {
      return;
    }
    dynamicData[rootPath] = null;

    const params = new URLSearchParams({
      'list-type': '2',
      //delimiter: '/',
      prefix: `hd-aem-archive${rootPath}/`,
    });

    const response = await fetch(`${this.S3_AEM_ROOT}/?${params.toString()}`);

    if (!response.ok) {
      return;
    }

    const xml = await response.text();

    const parser = new DOMParser();
    const parsedXml: XMLDocument = parser.parseFromString(
      xml,
      'application/xml',
    );

    const images: string[] = [];
    parsedXml.querySelectorAll('Contents > Key').forEach((key) => {
      if (!key.textContent || key.textContent.endsWith('.json')) {
        return;
      }

      images.push(key.textContent.replace('hd-aem-archive', ''));
    });

    this.dynamicFetchedData.update((responses) => {
      responses[rootPath] = images;

      return responses;
    });

    this.cd.detectChanges();
  }

  private buildMenu(
    page: object,
    currentPath: string,
    excludedPaths: string[],
    depth: number,
  ): MenuData[] {
    const pages: MenuData[] = [];

    Object.entries(page).forEach(([currentSlug, currentPage]) => {
      if (
        currentSlug.startsWith('jcr:') ||
        currentSlug.startsWith('cq:') ||
        currentSlug.startsWith('sling:')
      ) {
        return;
      }

      let currentMenuData: MenuData | undefined;

      // don't include pages that have been excluded (still scan home however)
      // otherwise include in markdown
      if (excludedPaths.includes(currentSlug)) {
        if (currentSlug !== 'home') {
          return;
        }
      } else {
        currentMenuData = {
          title: currentPage['jcr:content']?.['jcr:title'] ?? currentSlug,
          slug: currentSlug,
          path: `${currentPath}/${currentSlug}`.replace('/home', ''),
          hiddenInMenu: currentPage['jcr:content']?.disabledInTopNav === 'true',
          children: [],
        };

        pages.push(currentMenuData);
      }

      if (typeof currentPage === 'object') {
        // if the current page is the home page (or unset), add to root
        // otherwise add to the current page's children
        if (!currentMenuData) {
          pages.push(
            ...this.buildMenu(
              currentPage,
              `${currentPath}/${currentSlug}`,
              excludedPaths,
              depth + 1,
            ),
          );
        } else {
          currentMenuData.children.push(
            ...this.buildMenu(
              currentPage,
              `${currentPath}/${currentSlug}`,
              excludedPaths,
              depth + 1,
            ),
          );
        }
      }
    });

    return pages;
  }

  private async generateAemMenu() {
    const response = await fetch(
      `${this.CDN_AEM_ROOT}/content/dealer/en/${this.domainAemRootPath().replace('/home', '')}.menu.json`,
    );

    if (!response.ok) {
      this.snackbarService.openSnackBar({
        message: 'Error fetching AEM menu markdown',
        action: '',
        error: true,
      });
      return;
    }

    const json = await response.json();
    const excludedPaths = [
      ...(json['jcr:content']?.['cq:LiveSyncConfig']?.['cq:excludedPaths'] ??
        []),
      ...(json.home['jcr:content']?.['cq:LiveSyncConfig']?.[
        'cq:excludedPaths'
      ] ?? []),
    ];

    this.menuDataSource.data = this.buildMenu(json, '', excludedPaths, 0);
    this.menuTreeControl.dataNodes = this.menuDataSource.data;
  }

  async openAemMenuDialog() {
    this.generateAemMenu();

    this.formTemplateService
      .open(this.aemMenuTemplate, {
        width: '800px',
        data: {
          headerText: 'AEM Menu',
          buttonText: 'Close',
          hideCancelButton: true,
        },
      })
      .subscribe((dialog) => {
        dialog.afterClosed().subscribe(() => {
          this.menuDataSource.data = [];
          this.menuTreeControl.dataNodes = this.menuDataSource.data;
        });
      });
  }

  hasMenuSubpages(_: number, node: MenuData) {
    return !!node.children && node.children.length > 0;
  }
}
